import { useState } from 'react';
import { Search, Layers, Tag, Trash2, Save, BookMarked, Plus, CheckCircle2, AlertCircle } from 'lucide-react';
import { Modal, Input, Select } from '../../ui';
import { useBlocksStore, useEditorStore } from '../../store';
import { deepClone, generateId } from '../../utils';
import { cn } from '../../utils';
import type { Block, DesignColumn, DesignElement, DesignRow } from '../../types/editor.types';
import { clickable } from "components/ui/interactive";

function BlockCard({ block, onInsert, onDelete }: {
  block: Block;
  onInsert: (block: Block) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="group rounded-xl border border-line-subtle dark:border-dark-700 overflow-hidden
      hover:border-violet-300 dark:hover:border-violet-500/40
      hover:shadow-md hover:shadow-violet-100/50 dark:hover:shadow-violet-900/20
      transition-all duration-200 bg-surface dark:bg-dark-800">

      {/* Thumbnail area */}
      <div
        className="h-20 bg-gray-50 dark:bg-dark-900/60
          flex items-center justify-center cursor-pointer relative overflow-hidden"
        {...clickable(() => onInsert(block))}
        title={`Insert "${block.name}"`}
      >
        {block.thumbnail ? (
          <img src={block.thumbnail} alt={block.name} className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <div className="w-8 h-8 rounded-xl
              bg-violet-100 dark:bg-violet-900/30
              flex items-center justify-center">
              <Layers className="size-4 text-violet-500 dark:text-violet-400" />
            </div>
            <span className="text-[10px] font-semibold text-violet-400 dark:text-violet-500">
              Click to insert
            </span>
          </div>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-violet-500/0 group-hover:bg-violet-500/10
          flex items-center justify-center transition-all duration-200">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="bg-surface dark:bg-dark-800 text-violet-600 dark:text-violet-400
              text-xs font-bold px-2.5 py-1 rounded-full shadow-md border border-violet-200 dark:border-violet-700">
              + Insert
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 py-2 flex items-center justify-between
        border-t border-line-subtle dark:border-dark-700">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-gray-700 dark:text-white truncate">{block.name}</p>
          <p className="text-[10px] text-content-subtle dark:text-dark-500 mt-0.5 capitalize">{block.category}</p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(block.id); }}
          className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 ml-2 w-6 h-6 rounded-lg
            flex items-center justify-center shrink-0
            text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20
            transition-all duration-150"
          title="Delete block"
        >
          <Trash2 className="size-3" />
        </button>
      </div>
    </div>
  );
}

function SaveBlockModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { selectedRowId, design } = useEditorStore();
  const { addBlock } = useBlocksStore();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Custom');

  const selectedRow = design.body.rows.find((r) => r.id === selectedRowId);

  const handleSave = () => {
    if (!selectedRow || !name.trim()) return;
    addBlock(name.trim(), category, [], selectedRow);
    setName('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Save as Reusable Block"
      size="sm"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold rounded-xl
              text-content-muted dark:text-dark-400
              hover:bg-gray-100 dark:hover:bg-dark-700
              transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || !selectedRow}
            className={cn(
              'px-4 py-2 text-sm font-bold rounded-xl transition-all duration-150',
              'bg-gradient-to-r from-violet-500 to-indigo-500 text-white',
              'shadow-md shadow-violet-300/30 hover:shadow-lg hover:-translate-y-0.5',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-md'
            )}
          >
            Save Block
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Row status banner */}
        {!selectedRow ? (
          <div className="flex items-start gap-3 p-3.5 rounded-xl
            bg-amber-50 dark:bg-amber-900/20
            border border-amber-200 dark:border-amber-700/40">
            <AlertCircle className="size-4 text-amber-500 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-amber-700 dark:text-amber-400">No row selected</p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5 leading-relaxed">
                Click on a row in the canvas first, then open this dialog to save it.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3 p-3.5 rounded-xl
            bg-violet-50 dark:bg-violet-900/20
            border border-violet-200 dark:border-violet-700/40">
            <CheckCircle2 className="size-4 text-violet-500 dark:text-violet-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-violet-700 dark:text-violet-400">Row selected ✓</p>
              <p className="text-xs text-violet-500 dark:text-violet-500 mt-0.5">
                {selectedRow.columns.length} column{selectedRow.columns.length !== 1 ? 's' : ''} ·{' '}
                {selectedRow.columns.reduce((acc, c) => acc + c.elements.length, 0)} element
                {selectedRow.columns.reduce((acc, c) => acc + c.elements.length, 0) !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}

        <Input
          label="Block Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Hero Section"
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
        />
        <Select
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={[
            { value: 'Custom', label: 'Custom' },
            { value: 'Headers', label: 'Headers' },
            { value: 'Heroes', label: 'Heroes' },
            { value: 'Features', label: 'Features' },
            { value: 'CTAs', label: 'CTAs' },
            { value: 'Footers', label: 'Footers' },
          ]}
        />
      </div>
    </Modal>
  );
}

export function BlocksPanel() {
  const { blocks, removeBlock } = useBlocksStore();
  const { insertBlockRow } = useEditorStore();
  const [search, setSearch] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);

  const filtered = blocks.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase()) ||
    b.category.toLowerCase().includes(search.toLowerCase()) ||
    b.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()))
  );

  const categories = [...new Set(blocks.map((b) => b.category))];

  const handleInsert = (block: Block) => {
    const clonedRow: DesignRow = deepClone(block.design);
    clonedRow.id = generateId();
    clonedRow.columns = clonedRow.columns.map((col: DesignColumn) => ({
      ...col,
      id: generateId(),
      elements: col.elements.map((el: DesignElement) => ({ ...el, id: generateId() })),
    }));
    insertBlockRow(clonedRow);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search + Save action */}
      <div className="px-3 py-3 border-b border-line-subtle dark:border-dark-700 space-y-2.5">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-content-subtle dark:text-dark-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search blocks…"
            className="w-full h-8 pl-8 pr-3 text-xs rounded-lg
              border border-line dark:border-dark-600
              bg-gray-50 dark:bg-dark-900
              text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-dark-500
              focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400
              transition-all"
          />
        </div>

        {/* Premium save button */}
        <button
          onClick={() => setSaveOpen(true)}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl
            bg-gradient-to-r from-violet-500 to-indigo-500
            hover:from-violet-600 hover:to-indigo-600
            text-white text-xs font-bold
            shadow-md shadow-violet-300/30 dark:shadow-violet-900/30
            hover:shadow-lg hover:-translate-y-0.5
            transition-all duration-200"
        >
          <div className="w-5 h-5 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
            <Save className="size-3 text-white" />
          </div>
          <span className="flex-1 text-left">Save Selected Row as Block</span>
          <Plus className="size-3.5 text-white/70 shrink-0" />
        </button>
      </div>

      {/* Block list */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {blocks.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center h-full gap-4 py-10">
            <div className="relative">
            <div className="w-14 h-14 rounded-2xl
              bg-violet-100 dark:bg-violet-900/20
              border border-violet-200/50 dark:border-violet-700/30
              flex items-center justify-center">
              <BookMarked className="size-6 text-violet-500 dark:text-violet-400" />
            </div>
          </div>
            <div className="text-center">
              <p className="text-sm font-bold text-content-secondary dark:text-dark-300">No blocks saved yet</p>
              <p className="text-xs text-content-subtle dark:text-dark-500 mt-1.5 leading-relaxed max-w-[180px] mx-auto">
                Select a row on the canvas, then click "Save Selected Row as Block" above.
              </p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10">
            <Search className="size-7 text-gray-200 dark:text-dark-700 mx-auto mb-2" />
            <p className="text-sm text-content-subtle dark:text-dark-500">No blocks match "{search}"</p>
          </div>
        ) : (
          <div className="space-y-4">
            {categories.map((cat) => {
              const catBlocks = filtered.filter((b) => b.category === cat);
              if (!catBlocks.length) return null;
              return (
                <div key={cat}>
                  {/* Category label */}
                  <div className="flex items-center gap-2 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                    <div className="flex items-center gap-1.5">
                      <Tag className="size-3 text-content-subtle dark:text-dark-500" />
                      <p className="text-[11px] font-bold uppercase tracking-widest text-content-subtle dark:text-dark-500">
                        {cat}
                      </p>
                    </div>
                    <span className="ml-auto text-[10px] text-gray-300 dark:text-dark-600 font-medium">
                      {catBlocks.length}
                    </span>
                  </div>
                  {/* 2-col block grid */}
                  <div className="grid grid-cols-2 gap-2">
                    {catBlocks.map((block) => (
                      <BlockCard key={block.id} block={block} onInsert={handleInsert} onDelete={removeBlock} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SaveBlockModal open={saveOpen} onClose={() => setSaveOpen(false)} />
    </div>
  );
}
